import { useEffect, useState, type FormEvent } from "react";
import {
  waitlistApi,
  type FamilyNotificationPreferencesInput,
} from "./api.js";
import { useAsync } from "../useAsync.js";

export function FamilyOfferEmailPreferences({
  studentId,
}: {
  studentId: string;
}) {
  const loaded = useAsync(
    () => waitlistApi.notificationPreferences(studentId),
    [studentId],
  );
  const [offersEnabled, setOffersEnabled] = useState(true);
  const [practiceAssignmentBellEnabled, setPracticeAssignmentBellEnabled] = useState(false);
  const [weeklySummaryEnabled, setWeeklySummaryEnabled] = useState(false);
  const [concertInvitationEmailEnabled, setConcertInvitationEmailEnabled] = useState(false);
  const [quietHoursStart, setQuietHoursStart] = useState("");
  const [quietHoursEnd, setQuietHoursEnd] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!loaded.data?.enabled) return;
    setOffersEnabled(loaded.data.shortNoticeOffersEnabled);
    setPracticeAssignmentBellEnabled(loaded.data.practiceAssignmentBellEnabled);
    setWeeklySummaryEnabled(loaded.data.weeklySummaryEnabled);
    setConcertInvitationEmailEnabled(loaded.data.concertInvitationEmailEnabled);
    setQuietHoursStart(loaded.data.quietHoursStart ?? "");
    setQuietHoursEnd(loaded.data.quietHoursEnd ?? "");
    setSaveError(null);
    setSaved(false);
  }, [loaded.data]);

  if (!loaded.loading && !loaded.error && loaded.data?.enabled === false) {
    return null;
  }

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaveError(null);
    setSaved(false);
    if ((quietHoursStart === "") !== (quietHoursEnd === "")) {
      setSaveError("Choose both quiet-hours times or leave both blank.");
      return;
    }
    if (quietHoursStart !== "" && quietHoursStart === quietHoursEnd) {
      setSaveError("Quiet hours need a start and a different end time.");
      return;
    }

    const input: FamilyNotificationPreferencesInput = {
      shortNoticeOffersEnabled: offersEnabled,
      ...(loaded.data?.enabled === true && loaded.data.practiceAssignmentBellAvailable
        ? { practiceAssignmentBellEnabled }
        : {}),
      ...(loaded.data?.enabled === true && loaded.data.weeklySummaryAvailable
        ? { weeklySummaryEnabled }
        : {}),
      ...(loaded.data?.enabled === true && loaded.data.concertInvitationEmailAvailable
        ? { concertInvitationEmailEnabled }
        : {}),
      quietHoursStart: quietHoursStart || null,
      quietHoursEnd: quietHoursEnd || null,
    };
    setSaving(true);
    try {
      await waitlistApi.saveNotificationPreferences(studentId, input);
      setSaved(true);
    } catch (cause) {
      setSaveError(
        cause instanceof Error
          ? cause.message
          : "Could not save notification preferences.",
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <section aria-labelledby="notification-preferences-heading" className="panel">
      <h3 id="notification-preferences-heading">Notification preferences</h3>
      {loaded.loading && <p className="empty">Loading…</p>}
      {loaded.error && (
        <div className="error" role="alert">
          {String(loaded.error)}
          <button
            className="quiet small"
            onClick={loaded.reload}
            type="button"
          >
            Try again
          </button>
        </div>
      )}
      {loaded.data?.enabled && (
        <form onSubmit={(event) => void save(event)}>
          <p className="small muted">
            Short-notice offer email and quiet hours are shared for this
            learner. Practice bell, weekly summary, and concert invitation and programme
            update email choices apply only to this signed-in account for this learner.
            Times use Europe/London.
          </p>
          <label>
            <input
              checked={offersEnabled}
              disabled={saving || loaded.loading}
              onChange={(event) => setOffersEnabled(event.target.checked)}
              type="checkbox"
            />{" "}
            Email me when a short-notice lesson offer is available
          </label>
          {loaded.data.weeklySummaryAvailable && (
            <label>
              <input
                checked={weeklySummaryEnabled}
                disabled={saving || loaded.loading}
                onChange={(event) => setWeeklySummaryEnabled(event.target.checked)}
                type="checkbox"
              />{" "}
              Email this account a weekly summary of practice tasks set for this learner
            </label>
          )}
          {loaded.data.concertInvitationEmailAvailable && (
            <label>
              <input
                checked={concertInvitationEmailEnabled}
                disabled={saving || loaded.loading}
                onChange={(event) => setConcertInvitationEmailEnabled(event.target.checked)}
                type="checkbox"
              />{" "}
              Email this account about concert invitations and programme updates for this learner
            </label>
          )}
          {loaded.data.weeklySummaryAvailable && (
            <p className="small muted">
              Weekly summaries are queued on Monday after 09:00 Europe/London
              and link to tasks set during the previous calendar week. They are
              sent only while at least one of those tasks remains active;
              quiet hours still apply.
            </p>
          )}
          {loaded.data.practiceAssignmentBellAvailable && (
            <>
              <label>
                <input
                  checked={practiceAssignmentBellEnabled}
                  disabled={saving || loaded.loading}
                  onChange={(event) => setPracticeAssignmentBellEnabled(event.target.checked)}
                  type="checkbox"
                />{" "}
                Show a bell notification on this account when new practice is set for this learner
              </label>
              <p className="small muted">
                Practice assignment notices are in-app only; they do not send email.
              </p>
            </>
          )}
          <div className="form-row">
            <label>
              Quiet hours start
              <input
                aria-label="Quiet hours start"
                disabled={saving}
                onChange={(event) => setQuietHoursStart(event.target.value)}
                type="time"
                value={quietHoursStart}
              />
            </label>
            <label>
              Quiet hours end
              <input
                aria-label="Quiet hours end"
                disabled={saving}
                onChange={(event) => setQuietHoursEnd(event.target.value)}
                type="time"
                value={quietHoursEnd}
              />
            </label>
          </div>
          <p className="small muted">
            Leave both times blank for no quiet hours. Offer emails wait until
            quiet hours end; offers remain visible in Home.
          </p>
          {saveError && (
            <div className="error" role="alert">
              {saveError}
            </div>
          )}
          {saved && (
            <p aria-live="polite" className="small">
              Preferences saved.
            </p>
          )}
          <button className="quiet small" disabled={saving} type="submit">
            {saving ? "Saving…" : "Save preferences"}
          </button>
        </form>
      )}
    </section>
  );
}
